import React from 'react'
import styled from 'styled-components'

import FeatureBlock from '@/components/feature-block'
import Image from '@/components/image'
import RichText, {hasRichText} from '@/components/rich-text'
import {Stack} from '@/components/stack'
import {Text} from '@/components/text'
import {useGlobalStrings} from '@/providers/global-strings'
import CardGrid from '@/sections/card-grid'
import SignupBannerSection from '@/sections/signup-banner'
import {Section} from '@/styles/grid'
import {tokens} from '@/theme'
import getEnvironmentFormId from '@/utils/get-environment-field'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import mq from '@/utils/media-query'

import type {Props as FeatureBlockProps} from '@/components/feature-block'
import type {Props as RichTextProps} from '@/components/rich-text'
import type {SignupBannerFragment} from '@/graphql-types'
import type {FormConfig} from '@/types/consents'
import type {ImageSource} from '@/types/image'

interface Card {
    logo: ImageSource
    quote_author: string
    quote_author_image: ImageSource
    quote: string
    details: {
        title: string
        body: string
    }[]
}

export interface Props {
    data: {
        card: Card
        hero: FeatureBlockProps
        challengeTitle: string
        challengeLeadText: RichTextProps['content']
        challengeList: {
            challenge_list_item_title: string
            challenge_list_item_body: RichTextProps['content']
        }[]
        solutionTitle: string
        solutionLeadText: RichTextProps['content']
        resultTitle: string
        resultLeadText: RichTextProps['content']
        resultList: {
            result_list_item_title: string
            result_list_item_body: RichTextProps['content']
        }[]
        relatedCasesTitle?: React.ComponentProps<typeof CardGrid>['title']
        relatedCases?: React.ComponentProps<typeof CardGrid>['cards']
        relatedCasesShowMoreText?: React.ComponentProps<typeof CardGrid>['showMoreText']
        predefinedSignupBanner?: SignupBannerFragment['data']
    }
}

export const Showcase = (props: Props) => {
    const pageData = props.data
    const {show_more} = useGlobalStrings()

    if (!props.data) {
        return handleNoPrismicData()
    }

    return (
        <>
            <Section>
                <FeatureBlock
                    titleVariant="heading-large"
                    videoId={pageData.hero.videoId}
                    preTitle={pageData.hero.preTitle}
                    title={pageData.hero.title}
                    image={pageData.hero.image}
                />
            </Section>

            <Section>
                <ContentWrapper>
                    <CustomerCard>
                        {pageData.card.logo && <Image width={290} image={pageData.card.logo} />}
                        <CardDetails>
                            {pageData.card.details.map((detail, index) => (
                                <CardDetail key={`details-${index}`}>
                                    <Text as="h6" $variant="label-base-medium" $color="brand">
                                        {detail.title}
                                    </Text>
                                    <Text $variant="paragraph-base">{detail.body}</Text>
                                </CardDetail>
                            ))}
                        </CardDetails>

                        {hasRichText(pageData.card.quote) && (
                            <RichText
                                $variant="paragraph-base"
                                withQuotes
                                content={pageData.card.quote}
                            />
                        )}
                        {pageData.card.quote_author && (
                            <QuoteAuthor>
                                {pageData.card.quote_author_image && (
                                    <Image width={48} image={pageData.card.quote_author_image} />
                                )}
                                <Text $variant="label-base">{pageData.card.quote_author}</Text>
                            </QuoteAuthor>
                        )}
                    </CustomerCard>
                    <ContentLayout>
                        <ContentSection>
                            <Text as="h2" $variant="heading-base">
                                {pageData.challengeTitle}
                            </Text>
                            {hasRichText(pageData.challengeLeadText) && (
                                <RichText content={pageData.challengeLeadText} />
                            )}

                            <StyledList as="ul" $space={20}>
                                {pageData.challengeList.map((listItem, index) => (
                                    <li key={`challenge-item-${index}`}>
                                        <LiContent>
                                            <Text $variant="paragraph-large">
                                                {listItem.challenge_list_item_title}
                                            </Text>
                                            {hasRichText(listItem.challenge_list_item_body) && (
                                                <RichText
                                                    $variant="paragraph-base"
                                                    content={listItem.challenge_list_item_body}
                                                />
                                            )}
                                        </LiContent>
                                    </li>
                                ))}
                            </StyledList>
                        </ContentSection>

                        <ContentSection>
                            <Text as="h2" $variant="heading-base">
                                {pageData.solutionTitle}
                            </Text>
                            {hasRichText(pageData.solutionLeadText) && (
                                <RichText content={pageData.solutionLeadText} />
                            )}
                        </ContentSection>

                        <ContentSection>
                            <Text as="h2" $variant="heading-base">
                                {pageData.resultTitle}
                            </Text>
                            {hasRichText(pageData.resultLeadText) && (
                                <RichText content={pageData.resultLeadText} />
                            )}
                        </ContentSection>

                        <StyledList as="ul" $space={20}>
                            {pageData.resultList.map((listItem, index) => (
                                <li key={`result-item-${index}`}>
                                    <LiContent>
                                        <Text $variant="paragraph-large">
                                            {listItem.result_list_item_title}
                                        </Text>
                                        {hasRichText(listItem.result_list_item_body) && (
                                            <RichText
                                                $variant="paragraph-base"
                                                content={listItem.result_list_item_body}
                                            />
                                        )}
                                    </LiContent>
                                </li>
                            ))}
                        </StyledList>
                    </ContentLayout>
                </ContentWrapper>
            </Section>

            {pageData.relatedCases && pageData.relatedCases.length > 0 && (
                <CardGrid
                    title={pageData.relatedCasesTitle}
                    showMoreText={pageData.relatedCasesShowMoreText ?? show_more}
                    cards={pageData.relatedCases.map((card) => ({
                        image: card.image,
                        title: card.title,
                        body: card.body,
                        ctaText: card.ctaText,
                        cta_link: card.ctaLink,
                    }))}
                />
            )}

            {pageData.predefinedSignupBanner && (
                <SignupBannerSection
                    {...pageData.predefinedSignupBanner}
                    color="pink"
                    formConfig={
                        getEnvironmentFormId({
                            production: pageData.predefinedSignupBanner.form_production,
                            staging: pageData.predefinedSignupBanner.form_staging,
                        }) as FormConfig
                    }
                    placeholder={pageData.predefinedSignupBanner.email_input_placeholder}
                    submitText={pageData.predefinedSignupBanner.submit_text}
                    errorText={pageData.predefinedSignupBanner.email_input_error_text}
                />
            )}
        </>
    )
}

const ContentWrapper = styled.div`
    display: grid;
    gap: ${tokens.spacing48};
    align-items: start;

    ${mq.min('md')} {
        gap: 100px;
        grid-template-columns: 1fr auto;
    }

    ${mq.min('lg')} {
        gap: 190px;
    }
`

const ContentLayout = styled.div`
    display: grid;
    gap: ${tokens.spacing48};
`

const CustomerCard = styled.aside`
    display: grid;
    max-width: 376px;
    padding: ${tokens.spacing20};
    background: ${tokens.shade200};
    border-radius: ${tokens.arc20};
    gap: ${tokens.spacing32};
    justify-items: center;
    justify-self: center;

    ${mq.min('sm')} {
        position: sticky;
        top: 25vh;
        grid-column-start: 2;
        grid-row-start: 1;
    }
`

const CardDetails = styled.div`
    display: grid;
    width: 100%;
    gap: ${tokens.spacing20};
`

const QuoteAuthor = styled.blockquote`
    display: grid;
    grid-template-columns: 48px auto;
    align-items: center;
    gap: ${tokens.spacing20};
    width: 100%;
`
const CardDetail = styled.div`
    display: grid;
    width: 100%;
    gap: ${tokens.spacing4};
`

const ContentSection = styled.div`
    display: grid;
    gap: ${tokens.spacing32};
`

const LiContent = styled.div`
    display: grid;
    gap: ${tokens.spacing12};
`

const StyledList = styled(Stack)`
    li {
        margin-left: ${tokens.spacing40};
        list-style: disc;

        &::marker {
            font-size: ${tokens.font3XLarge};
        }
    }
`
